import { useState } from "react";
import { toast } from "sonner";
import {
  User,
  Lock,
  Mail,
  Phone,
  Eye,
  EyeOff,
  Save,
  Shield,
} from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { usersApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const AdminProfilePage = () => {
  const { user, login } = useAuth();
  const [activeTab, setActiveTab] = useState("profile"); // profile | password
  
  // Profile form
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    username: user?.username || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });
  const [profileSaving, setProfileSaving] = useState(false);

  // Password form
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const updated = await usersApi.updateProfile(profileData);
      toast.success("Profil berhasil diperbarui");
      // Update auth context
      if (updated && updated.email) {
        // Refresh current user data in context
        window.location.reload();
      }
    } catch (err) {
      toast.error(err.message || "Gagal memperbarui profil");
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    setPasswordSaving(true);
    try {
      await usersApi.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success("Password berhasil diubah");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err.message || "Gagal mengubah password");
    } finally {
      setPasswordSaving(false);
    }
  };

  const ROLE_LABELS = {
    superadmin: { label: "Superadmin", color: "bg-amber-100 text-amber-700" },
    admin: { label: "Admin", color: "bg-emerald-100 text-emerald-700" },
    owner: { label: "Owner", color: "bg-purple-100 text-purple-700" },
  };
  const roleInfo = ROLE_LABELS[user?.role] || ROLE_LABELS.admin;

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl" data-testid="admin-profile-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-stone-900 flex items-center gap-2">
            <User className="w-7 h-7 text-emerald-600" />
            Pengaturan Profil
          </h1>
          <p className="text-stone-500 mt-1">Kelola informasi akun dan keamanan Anda</p>
        </div>

        {/* User card */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <span className="text-2xl font-semibold text-emerald-700">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-stone-900">{user?.name}</h2>
              <p className="text-stone-500">{user?.email}</p>
              <span className={`inline-flex items-center gap-1 mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${roleInfo.color}`}>
                <Shield className="w-3 h-3" />
                {roleInfo.label}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-stone-100 rounded-xl mb-6">
          <button
            onClick={() => setActiveTab("profile")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "profile"
                ? "bg-white text-stone-900 shadow-sm"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            Informasi Profil
          </button>
          <button
            onClick={() => setActiveTab("password")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "password"
                ? "bg-white text-stone-900 shadow-sm"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            Ganti Password
          </button>
        </div>

        {/* Profile tab */}
        {activeTab === "profile" && (
          <form onSubmit={handleProfileSubmit} className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Nama Lengkap</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="text"
                  required
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="w-full h-14 pl-10 pr-4 border border-stone-200 rounded-xl text-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="input-profile-name"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="text"
                  value={profileData.username}
                  onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                  placeholder="username (untuk login)"
                  className="w-full h-14 pl-10 pr-4 border border-stone-200 rounded-xl text-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="input-profile-username"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="email"
                  required
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  className="w-full h-14 pl-10 pr-4 border border-stone-200 rounded-xl text-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="input-profile-email"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">No. Telepon</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="text"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  placeholder="08xxxxxxxxxx"
                  className="w-full h-14 pl-10 pr-4 border border-stone-200 rounded-xl text-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="input-profile-phone"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={profileSaving}
              className="inline-flex items-center gap-2 h-14 px-6 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
              data-testid="btn-save-profile"
            >
              <Save className="w-5 h-5" />
              {profileSaving ? "Menyimpan..." : "Simpan Perubahan"}
            </button>
          </form>
        )}

        {/* Password tab */}
        {activeTab === "password" && (
          <form onSubmit={handlePasswordSubmit} className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Password Saat Ini</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type={showCurrentPw ? "text" : "password"}
                  required
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="w-full h-14 pl-10 pr-12 border border-stone-200 rounded-xl text-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="input-current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPw(!showCurrentPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showCurrentPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Password Baru</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type={showNewPw ? "text" : "password"}
                  required
                  minLength={6}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="w-full h-14 pl-10 pr-12 border border-stone-200 rounded-xl text-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="input-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPw(!showNewPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showNewPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Konfirmasi Password Baru</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type={showNewPw ? "text" : "password"}
                  required
                  minLength={6}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="w-full h-14 pl-10 pr-4 border border-stone-200 rounded-xl text-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="input-confirm-password"
                />
              </div>
              {passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password && (
                <p className="text-sm text-rose-600 mt-1">Password tidak cocok</p>
              )}
            </div>
            <button
              type="submit"
              disabled={passwordSaving}
              className="inline-flex items-center gap-2 h-14 px-6 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
              data-testid="btn-change-password"
            >
              <Lock className="w-5 h-5" />
              {passwordSaving ? "Mengubah..." : "Ubah Password"}
            </button>
          </form>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProfilePage;
